"use client";

/**
 * UsernameStep Component
 *
 * First step in the setup flow. User enters their Instagram/TikTok username.
 * Features:
 * - Animated platform toggle (Instagram/TikTok)
 * - Reuses InstagramSearchField/TiktokSearchField with autocomplete
 * - Uses Outfame Button component for consistent styling
 * - Prefetches assets for LoadingOverlay (step 2) after page load
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import InstagramSearchField from "@/components/presentational/tailwind/InstagramSearchField";
import TiktokSearchField from "@/components/presentational/tailwind/TikTokSearchField";
import Button from "@/components/outfame/Button";
import PlatformToggle from "@/components/outfame/PlatformToggle";
import { normalizeSocialHandle, HANDLE_RULES } from "@/utils/social-handle";
import { T, useGT, useLocale } from "gt-next";

/** Polished shield icon with subtle emerald gradient */
function ShieldGradientIcon(t0) {
  const $ = _c(4);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "h-4 w-4" : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <defs><linearGradient id="shield-emerald-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#059669" /></linearGradient></defs>;
    t3 = <path fillRule="evenodd" clipRule="evenodd" d="M9.661 2.237a.75.75 0 0 1 .678 0 12.013 12.013 0 0 0 5.322 1.24.75.75 0 0 1 .739.665c.1.813.149 1.643.149 2.487 0 4.543-2.475 8.485-6.136 10.596a.75.75 0 0 1-.826 0C5.925 15.114 3.45 11.172 3.45 6.629c0-.844.05-1.674.149-2.487a.75.75 0 0 1 .74-.665 12.013 12.013 0 0 0 5.322-1.24Zm3.593 5.517a.75.75 0 0 0-1.008-1.108l-3.09 2.81-1.092-.993a.75.75 0 1 0-1.008 1.108l1.6 1.455a.75.75 0 0 0 1.008 0l3.59-3.272Z" fill="url(#shield-emerald-gradient)" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== className) {
    t4 = <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">{t2}{t3}</svg>;
    $[2] = className;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
import type { Platform } from "../../types";
import { phFieldTyped } from "@/lib/analytics/posthog-client";

/**
 * Prefetch critical images for LoadingOverlay after page is idle
 * This ensures the logo is ready when transitioning to step 2
 */
function usePrefetchLoadingAssets() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  const prefetchImages = _temp;
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(prefetchImages, {
      timeout: 2000
    });
    return () => window.cancelIdleCallback(id);
  } else {
    const timeout = setTimeout(prefetchImages, 1000);
    return () => clearTimeout(timeout);
  }
}
function _temp() {
  const logo = new window.Image();
  logo.src = "/img/outfame-logo-mark.svg";
}
interface UsernameStepProps {
  platform: Platform;
  username: string;
  isLoading: boolean;
  error: string | null;
  onPlatformChange: (platform: Platform) => void;
  onUsernameChange: (username: string) => void;
  onAvatarUrlChange: (avatarUrl: string | null) => void;
  onSubmit: () => void;
  onTrackUsername?: (username: string, platform: Platform) => void;
  /** Existing usernames for duplicate detection (for logged-in users adding new accounts) */
  existingUsernames?: Set<string>;
}
export default function UsernameStep(t0) {
  const $ = _c(59);
  const {
    platform,
    username,
    isLoading,
    error,
    onPlatformChange,
    onUsernameChange,
    onAvatarUrlChange,
    onSubmit,
    onTrackUsername,
    existingUsernames
  } = t0;
  const [localError, setLocalError] = useState(null);
  const gt = useGT();
  const locale = useLocale();
  let t1;
  if ($[0] !== gt || $[1] !== locale || $[2] !== platform) {
    t1 = (reason, message) => {
      if (locale === "en") {
        return message;
      }
      const label = HANDLE_RULES[platform].label;
      const hint = gt("Enter your {platform} username, like \"outfame\" \u2014 not a link.", {
        platform: label
      });
      switch (reason) {
        case "empty":
          {
            return gt("Enter your {platform} username.", {
              platform: label
            });
          }
        case "too_short":
          {
            return gt("That {platform} username is too short \u2014 it needs at least {min} characters.", {
              platform: label,
              min: HANDLE_RULES[platform].min
            });
          }
        case "too_long":
          {
            return gt("That {platform} username is too long \u2014 the maximum is {max} characters.", {
              platform: label,
              max: HANDLE_RULES[platform].max
            });
          }
        case "invalid_characters":
          {
            return message.includes("end with a period") ? gt("A {platform} username can't end with a period.", {
              platform: label
            }) : `${hint} ${gt("Usernames only contain letters, numbers, periods and underscores.")}`;
          }
        case "platform_mismatch":
          {
            return `${gt("That looks like a {platform} link.", {
              platform: platform === "instagram" ? "TikTok" : "Instagram"
            })} ${hint}`;
          }
        default:
          {
            return hint;
          }
      }
    };
    $[0] = gt;
    $[1] = locale;
    $[2] = platform;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleErrorMessage = t1;
  usePrefetchLoadingAssets();
  let t2;
  if ($[4] !== existingUsernames || $[5] !== gt || $[6] !== handleErrorMessage || $[7] !== onSubmit || $[8] !== onTrackUsername || $[9] !== onUsernameChange || $[10] !== platform || $[11] !== username) {
    t2 = () => {
      const result = normalizeSocialHandle(username, platform);
      if (!result.ok) {
        setLocalError(handleErrorMessage(result.reason, result.message));
        return;
      }
      const cleanUsername = result.handle;
      if (existingUsernames?.has(result.normalized)) {
        setLocalError(gt("You've already added @{username} to your account", {
          username: cleanUsername
        }));
        return;
      }
      setLocalError(null);
      if (cleanUsername !== username) {
        onUsernameChange(cleanUsername);
      }
      phFieldTyped("handle", cleanUsername);
      if (onTrackUsername) {
        onTrackUsername(cleanUsername, platform);
      }
      onSubmit();
    };
    $[4] = existingUsernames;
    $[5] = gt;
    $[6] = handleErrorMessage;
    $[7] = onSubmit;
    $[8] = onTrackUsername;
    $[9] = onUsernameChange;
    $[10] = platform;
    $[11] = username;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const runSubmit = t2;
  let t3;
  if ($[13] !== runSubmit) {
    t3 = e => {
      e.preventDefault();
      runSubmit();
    };
    $[13] = runSubmit;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const handleSubmit = t3;
  let t4;
  if ($[15] !== onUsernameChange) {
    t4 = value => {
      const cleanValue = value.replace(/^@+/, "");
      onUsernameChange(cleanValue);
      setLocalError(null);
    };
    $[15] = onUsernameChange;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleUsernameChange = t4;
  let t5;
  if ($[17] !== onAvatarUrlChange) {
    t5 = result_0 => {
      if (result_0 && "username" in result_0) {
        onAvatarUrlChange(result_0.profile_pic_url || result_0.avatar_url || null);
      } else {
        if (result_0 && "unique_id" in result_0) {
          onAvatarUrlChange(result_0.avatar_url || null);
        }
      }
    };
    $[17] = onAvatarUrlChange;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleResultSelected = t5;
  let t6;
  if ($[19] !== platform) {
    t6 = e_0 => {
      const target = e_0.target;
      if (!(target instanceof HTMLInputElement) || target.type === "password" || !target.value.trim()) {
        return;
      }
      const parsed = normalizeSocialHandle(target.value, platform);
      phFieldTyped("handle", parsed.ok ? parsed.handle : target.value);
    };
    $[19] = platform;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleFieldBlur = t6;
  const displayError = error || localError;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h1 className="text-[28px] font-bold leading-tight tracking-tight text-ink lg:text-[32px]"><T>Let's grow your account</T></h1>;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== platform) {
    t8 = <div className="mb-8 text-center lg:text-left">{t7}<p className="mt-3 text-base text-black/60">{platform === "instagram" ? <T>Enter your Instagram username and we'll analyze your account to create a personalized growth strategy.</T> : <T>Enter your TikTok username and we'll analyze your account to create a personalized growth strategy.</T>}</p></div>;
    $[22] = platform;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== onPlatformChange || $[25] !== platform) {
    t9 = <div className="mb-6"><PlatformToggle platform={platform} onPlatformChange={onPlatformChange} fullWidth={true} /></div>;
    $[24] = onPlatformChange;
    $[25] = platform;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== handleResultSelected || $[28] !== handleUsernameChange || $[29] !== isLoading || $[30] !== platform || $[31] !== runSubmit || $[32] !== username) {
    t10 = platform === "instagram" ? <InstagramSearchField onValueChange={handleUsernameChange} onResultSelected={handleResultSelected} onEnterKeyPressed={runSubmit} disabled={isLoading} autoFocus={true} defaultValue={username} /> : <TiktokSearchField onValueChange={handleUsernameChange} onResultSelected={handleResultSelected} onEnterKeyPressed={runSubmit} disabled={isLoading} autoFocus={true} defaultValue={username} />;
    $[27] = handleResultSelected;
    $[28] = handleUsernameChange;
    $[29] = isLoading;
    $[30] = platform;
    $[31] = runSubmit;
    $[32] = username;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== displayError) {
    t11 = displayError && <motion.p initial={{
      opacity: 0,
      y: -5
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-2 text-sm text-red-500">{displayError}</motion.p>;
    $[34] = displayError;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== isLoading || $[40] !== username) {
    t13 = isLoading || !username.trim();
    $[39] = isLoading;
    $[40] = username;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== gt || $[43] !== isLoading) {
    t14 = isLoading ? gt("Analyzing account...") : gt("Start Growing");
    $[42] = gt;
    $[43] = isLoading;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <Button type="submit" variant="primary" size="lg" disabled={t13} className="w-full">{t14}</Button>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== handleFieldBlur || $[49] !== handleSubmit || $[50] !== t12 || $[51] !== t15) {
    t16 = <form onSubmit={handleSubmit} onBlurCapture={handleFieldBlur} className="flex flex-col gap-6">{t12}{t15}</form>;
    $[48] = handleFieldBlur;
    $[49] = handleSubmit;
    $[50] = t12;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ShieldGradientIcon className="h-4 w-4" />;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="mt-auto pt-8"><div className="flex items-center justify-center gap-2">{t17}<p className="text-xs text-black/50"><T>No password required. Your account stays secure.</T></p></div></div>;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t16 || $[56] !== t8 || $[57] !== t9) {
    t19 = <div className="flex flex-1 flex-col pt-4 lg:pt-8">{t8}{t9}{t16}{t18}</div>;
    $[55] = t16;
    $[56] = t8;
    $[57] = t9;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}